import {
    DeviceEligibility,
    readExtensionSignal,
    resolveDeviceEligibility,
    STRICT_DEVICE_ELIGIBILITY,
} from '@/composables/useLiveSessionEligibility.js';
import BasePolicy from '@/policies/BasePolicy.js';
import store from '@/store/Index.js';
import { isCourseEditingOverrideEnabled } from '@/utilities/courseEditingOverride.js';
import { userHasFeature } from '@/utilities/userHasFeature.js';

export default class TimePeriodPolicy extends BasePolicy {
    viewAny(user) {
        return user.hasPermissionTo('view all time periods');
    }

    view(user, timePeriod) {
        const course = timePeriod.course || timePeriod.section.course;

        if (!course) {
            throw new Error('Time period must contain a reference to its course');
        }

        return user.hasPermissionViaCourseTo(course, 'view a time period');
    }

    viewCheckIn(user, timePeriod) {
        if (!timePeriod.course_id) {
            throw new Error('Time period must contain a reference to its course');
        }

        if (user.isInstructorOf(timePeriod.course_id)) {
            return true;
        }

        // Without ATTEND a student keeps the existing rule: the extension's device asset id alone shows the card. The
        // managed-Chromebook check (Student Story 25) applies to ATTEND users only, so an extension update that starts
        // reporting managed status cannot hide the card from anyone outside the rollout.
        if (!userHasFeature(user, store.state?.features?.ATTEND)) {
            return Boolean(store.getters.deviceAssetId);
        }

        return (
            resolveDeviceEligibility(
                { deviceAssetId: store.getters.deviceAssetId, managedStatus: readExtensionSignal().managedStatus },
                { legacyDeviceIdIsEligible: !STRICT_DEVICE_ELIGIBILITY }
            ) === DeviceEligibility.ELIGIBLE
        );
    }

    create(user, course) {
        return user.hasPermissionViaCourseTo(course, 'create a time period');
    }

    update(user, timePeriod) {
        if (!timePeriod.can_be_modified && !this.hasEditingOverride(user, timePeriod)) {
            return false;
        }

        if (user.is_admin) {
            return true;
        }

        const course = timePeriod.course || timePeriod.section.course;

        return user.hasPermissionViaCourseTo(course, 'edit a time period');
    }

    delete(user, timePeriod) {
        if (!timePeriod.can_be_modified && !this.hasEditingOverride(user, timePeriod)) {
            return false;
        }

        if (user.is_admin) {
            return true;
        }

        const course = timePeriod.course || timePeriod.section.course;

        return user.hasPermissionViaCourseTo(course, 'delete a time period');
    }

    hasEditingOverride(user, timePeriod) {
        const course = timePeriod.course || timePeriod.section?.course;
        return user.is_admin && !timePeriod.external_id && isCourseEditingOverrideEnabled(course?.editing_override);
    }

    modify(user, timePeriod) {
        return this.update(user, timePeriod) || this.delete(user, timePeriod);
    }
}
